import React, { FC, useEffect, useState } from 'react';

import { getLSValue, setLSValue } from '../../utils/locale-storage';
import { growthbook } from './growthbook';
import { sentry } from '../sentry/sentry';
import { USER_DATA } from '../../constants/common';
import { UserLocalStorageData } from '../../types/user';
import useQueryParams from '../../hooks/use-query-params';
import { checkIsComplianceMode } from '../../middlewares/app-mode/helpers';
import { PAYWALL_PLANS, PAYWALL_PLANS_NO_TRIAL, PAYWALL_VERSION } from './features';
import analytics from '../analytics';

const NO_TRIAL_ENDING = 'no_trial';
const CUSTOM_PLANS_ID_KEY = 'plan_price_id';
const AB_START = 'ab_start';

const getFeatureUserProperties = () => {
  const excludedKeys = new Set([PAYWALL_PLANS_NO_TRIAL, PAYWALL_PLANS]);

  const featureUserProperties = Object.keys(growthbook.getFeatures())
    .filter((key) => !excludedKeys.has(key))
    .reduce<Record<string, unknown>>((acc, key) => {
      const feature = growthbook.evalFeature(key);
      if (feature) {
        acc[key] = feature.value?.amplitude_key || feature.value;
      }
      return acc;
    }, {});

  const paywallVersion = featureUserProperties[PAYWALL_VERSION];

  const selectedPaywallKey =
    typeof paywallVersion === 'string' && paywallVersion.includes(NO_TRIAL_ENDING)
      ? PAYWALL_PLANS_NO_TRIAL
      : PAYWALL_PLANS;

  featureUserProperties[CUSTOM_PLANS_ID_KEY] = growthbook.evalFeature(selectedPaywallKey)?.value?.amplitude_key;

  return featureUserProperties;
};

type Props = {
  children: React.ReactNode;
};

const GrowthbookMiddleware: FC<Props> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);

  const { getQueryParams } = useQueryParams();

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const userLSData = getLSValue(USER_DATA, true) as UserLocalStorageData;
        const queryParams = getQueryParams();
        const userId = userLSData?.user_id || (queryParams?.customer_id as string) || '';
        const quizMode = checkIsComplianceMode() ? 'compliance' : 'force_quiz';

        await growthbook.init({
          ...(userId ? { user_id: userId } : {}),
          device_id: window.navigator.userAgent,
          device_language: window.navigator.language?.toLowerCase() || 'en',
          browser: window.navigator.userAgent,
          url: window.location.host,
        });

        growthbook.startAbTests();

        analytics.setUserProperties({ ...getFeatureUserProperties(), customer_id: userId });

        if (getLSValue(AB_START, true)) {
          return;
        }

        analytics.trackEvent(AB_START, {
          quiz_mode: quizMode,
        });
        setLSValue(AB_START, true);
      } catch (e: unknown) {
        console.error(e);
        sentry.captureException(e, {
          extra: {
            message: 'Failed to init Growthbook',
          },
        });
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return null;
  }

  return <>{children}</>;
};

export { GrowthbookMiddleware };
